// ============================================================================
// REDUCER - simulatorReducer.js
// ============================================================================
// ./context/simulator/simulatorReducer.js

const simulatorReducer = (state, { type, payload }) => {
    switch (type) {
        // Simulator Management
        case 'SET_SIMULATORS':
            return {
                ...state,
                simulators: payload,
                isLoading: false,
                error: null
            };
        
        case 'SET_CURRENT_SIMULATOR':
            return {
                ...state,
                currentSimulator: payload,
                isLoading: false,
                error: null
            };
        
        case 'ADD_SIMULATOR':
            return {
                ...state,
                simulators: [payload, ...state.simulators],
                isLoading: false,
                error: null
            };
        
        case 'UPDATE_SIMULATOR':
            return {
                ...state,
                simulators: state.simulators.map(simulator =>
                    simulator._id === payload._id ? payload : simulator
                ),
                currentSimulator: state.currentSimulator?._id === payload._id 
                    ? payload 
                    : state.currentSimulator,
                isLoading: false,
                error: null
            };
        
        case 'DELETE_SIMULATOR':
            return {
                ...state,
                simulators: state.simulators.filter(simulator => simulator._id !== payload),
                currentSimulator: state.currentSimulator?._id === payload 
                    ? null 
                    : state.currentSimulator,
                isLoading: false,
                error: null
            };

        // Simulator Control
        case 'START_SIMULATOR':
            return {
                ...state,
                simulators: state.simulators.map(simulator =>
                    simulator._id === payload._id ? { ...payload, status: 'running' } : simulator
                ),
                currentSimulator: state.currentSimulator?._id === payload._id 
                    ? { ...payload, status: 'running' }
                    : state.currentSimulator,
                error: null
            };

        case 'STOP_SIMULATOR':
            return {
                ...state,
                simulators: state.simulators.map(simulator =>
                    simulator._id === payload._id ? { ...payload, status: 'stopped' } : simulator
                ),
                currentSimulator: state.currentSimulator?._id === payload._id 
                    ? { ...payload, status: 'stopped' }
                    : state.currentSimulator,
                error: null
            };

        case 'PAUSE_SIMULATOR':
            return {
                ...state,
                simulators: state.simulators.map(simulator =>
                    simulator._id === payload._id ? { ...payload, status: 'paused' } : simulator
                ),
                currentSimulator: state.currentSimulator?._id === payload._id 
                    ? { ...payload, status: 'paused' }
                    : state.currentSimulator,
                error: null
            };

        case 'RESET_SIMULATOR':
            return {
                ...state,
                simulators: state.simulators.map(simulator =>
                    simulator._id === payload._id ? payload : simulator
                ),
                currentSimulator: state.currentSimulator?._id === payload._id 
                    ? payload 
                    : state.currentSimulator,
                serialOutput: state.currentSimulator?._id === payload._id ? [] : state.serialOutput,
                sensorReadings: state.currentSimulator?._id === payload._id ? {} : state.sensorReadings,
                gpioStates: state.currentSimulator?._id === payload._id ? {} : state.gpioStates,
                error: null
            };

        case 'UPDATE_STATUS':
            if (state.currentSimulator) {
                return {
                    ...state,
                    currentSimulator: {
                        ...state.currentSimulator,
                        ...payload
                    }
                };
            }
            return state;

        // Sensor Operations
        case 'ATTACH_SENSOR':
            return {
                ...state,
                simulators: state.simulators.map(simulator =>
                    simulator._id === payload._id ? payload : simulator
                ),
                currentSimulator: state.currentSimulator?._id === payload._id 
                    ? payload 
                    : state.currentSimulator,
                error: null
            };

        case 'DETACH_SENSOR':
            if (state.currentSimulator) {
                return {
                    ...state,
                    currentSimulator: {
                        ...state.currentSimulator,
                        attachedSensors: state.currentSimulator.attachedSensors.filter(
                            s => s.sensorId !== payload.sensorId
                        )
                    }
                };
            }
            return state;

        case 'UPDATE_SENSOR_VALUE':
            return {
                ...state,
                sensorReadings: {
                    ...state.sensorReadings,
                    [payload.sensorId]: {
                        value: payload.value,
                        timestamp: payload.timestamp
                    }
                }
            };

        // GPIO Operations
        case 'UPDATE_GPIO_STATE':
            return {
                ...state,
                gpioStates: {
                    ...state.gpioStates,
                    [payload.pin]: payload.value
                }
            };

        case 'UPDATE_GPIO_CONFIG':
            if (state.currentSimulator) {
                return {
                    ...state,
                    currentSimulator: {
                        ...state.currentSimulator,
                        gpioPins: state.currentSimulator.gpioPins.map(p =>
                            p.pinNumber === payload.pin ? payload : p
                        )
                    }
                };
            }
            return state;

        // WiFi Operations
        case 'CONNECT_WIFI':
            if (state.currentSimulator) {
                return {
                    ...state,
                    currentSimulator: {
                        ...state.currentSimulator,
                        wifiConfig: {
                            ...state.currentSimulator.wifiConfig,
                            ...payload,
                            connected: true
                        }
                    }
                };
            }
            return state;

        case 'DISCONNECT_WIFI':
            if (state.currentSimulator) {
                return {
                    ...state,
                    currentSimulator: {
                        ...state.currentSimulator,
                        wifiConfig: {
                            ...state.currentSimulator.wifiConfig,
                            connected: false,
                            ipAddress: ''
                        }
                    }
                };
            }
            return state;

        // Serial Output
        case 'SET_SERIAL_OUTPUT':
            return {
                ...state,
                serialOutput: payload,
                error: null
            };

        // Serial Input
        case 'SET_SERIAL_INPUT':
            return {
                ...state,
                serialOutput: payload,
                error: null
            };

        case 'ADD_SERIAL_LOG':
            return {
                ...state,
                serialOutput: [...state.serialOutput, payload]
            };

        // Filter-based lists
        case 'SET_STUDENT_SIMULATORS':
            return {
                ...state,
                studentSimulators: payload,
                isLoading: false,
                error: null
            };

        case 'SET_LAB_SIMULATORS':
            return {
                ...state,
                labSimulators: payload,
                isLoading: false,
                error: null
            };

        case 'SET_COURSE_SIMULATORS':
            return {
                ...state,
                courseSimulators: payload,
                isLoading: false,
                error: null
            };

        case 'SET_RUNNING_SIMULATORS':
            return {
                ...state,
                runningSimulators: payload,
                isLoading: false,
                error: null
            };

        case 'SET_ACTIVE_COUNT':
            return {
                ...state,
                activeCount: payload,
                error: null
            };

        // Loading & Error
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: payload
            };

        case 'SET_ERROR':
            return {
                ...state,
                error: payload,
                isLoading: false
            };

        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };

        case 'CLEAR_CURRENT_SIMULATOR':
            return {
                ...state,
                currentSimulator: null,
                serialOutput: [],
                sensorReadings: {},
                gpioStates: {}
            };

        case 'CLEAR_SIMULATOR_DATA':
            return {
                simulators: [],
                currentSimulator: null,
                studentSimulators: [],
                labSimulators: [],
                courseSimulators: [],
                runningSimulators: [],
                activeCount: 0,
                serialOutput: [],
                sensorReadings: {},
                gpioStates: {},
                isLoading: false,
                error: null
            };

        default:
            return state;
    }
};

export default simulatorReducer;